import { useApiErrorMessage, useCommonT } from '@mai-team-amusement/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

/** Spacing steps in MUI theme units. */
export const Space = {
  Xxs: 0.5,
  Xs: 1,
  Sm: 2,
  Md: 3,
} as const;

export function PageHeader({ title, actions }: { title: string; actions?: ReactNode }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={Space.Sm}
      sx={{ mb: Space.Md, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
    >
      <Typography variant="h5" component="h2">
        {title}
      </Typography>
      {actions === undefined ? null : (
        <Stack direction="row" spacing={Space.Xs} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

export function Section({
  title,
  children,
  actions,
}: {
  title?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Card component="section">
      <CardContent>
        <Stack spacing={Space.Sm}>
          {title === undefined && actions === undefined ? null : (
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              {title === undefined ? <span /> : <Typography variant="h6">{title}</Typography>}
              {actions}
            </Stack>
          )}
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Spinner while loading, the error message on failure, else the content. */
export function QueryView<T>({
  query,
  children,
}: {
  query: { data: T | undefined; error: unknown; isPending: boolean };
  children: (data: T) => ReactNode;
}) {
  const errorMessage = useApiErrorMessage();
  const common = useCommonT();
  if (query.data !== undefined) {
    return <>{children(query.data)}</>;
  }
  if (query.error !== null && query.error !== undefined) {
    return <Alert severity="error">{errorMessage(query.error)}</Alert>;
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: Space.Md }}>
      <CircularProgress aria-label={common('status.loading')} />
    </Box>
  );
}

export function ErrorAlert({ error }: { error: unknown }) {
  const errorMessage = useApiErrorMessage();
  return error === null || error === undefined ? null : (
    <Alert severity="error">{errorMessage(error)}</Alert>
  );
}
